import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy } from "lucide-react";
import { Card, PageHeader, Field, inputCls, Btn, CheckboxGroup, RadioGroupPills } from "../../components/dash/Kit";
import AiNotice from "../../components/dash/AiNotice";
import UsageMeter, { useUsage } from "../../components/dash/UsageMeter";
import VerifyNotice from "../../components/dash/VerifyNotice";
import CaptionEditor from "../../components/dash/CaptionEditor";
import { api, errMessage } from "../../lib/api";

const TYPES = ["Promote Product", "Promote Service", "Announcement", "Event", "Offer",
  "Educational", "Behind The Scenes", "Customer Review", "Other"];
const PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "TikTok"];

export default function Create() {
  const { usage, reloadUsage } = useUsage();
  const [type, setType] = useState("Promote Product");
  const [instruction, setInstruction] = useState("");
  const [platforms, setPlatforms] = useState(["Instagram"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!instruction.trim()) {
      toast.error("Tell PostMint what the post should be about.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/engine/quick-post", {
        post_type: type, instruction, platforms,
      });
      setResult(data);
      reloadUsage();
      toast.success("Your posts are ready to review.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const updateCaption = (id, caption) =>
    setResult((r) => ({ ...r, items: r.items.map((i) => (i.id === id ? { ...i, caption } : i)) }));

  return (
    <div data-testid="create-page">
      <PageHeader
        testId="create-header"
        title="Create a post"
        body="Say it the way you'd say it to a colleague. PostMint writes a version for every platform you pick."
      />

      <div className="mb-6">
        <UsageMeter usage={usage} />
      </div>

      <AiNotice className="mb-6" testId="create-ai-notice" />

      <Card testId="create-form-card">
        <Field label="What kind of post is this?">
          <RadioGroupPills name="create-type" options={TYPES} value={type} onChange={setType} />
        </Field>

        <div className="mt-5">
          <Field label="What should it say?" hint="Example: “Promote our new spicy chicken burger for €14.95.”">
            <textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={3}
              data-testid="create-instruction-input"
              className={inputCls}
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Where should it go?">
            <CheckboxGroup name="create-platforms" options={PLATFORMS} value={platforms} onChange={setPlatforms} />
          </Field>
        </div>

        <div className="mt-6">
          <Btn onClick={generate} disabled={busy} testId="create-generate-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Creating your posts…" : "Create My Posts"}
          </Btn>
        </div>
      </Card>

      {result && (
        <div className="mt-6 space-y-5" data-testid="create-result">
          <VerifyNotice items={result.verify} testId="create-verify-notice" />
          {result.items.map((item) => (
            <Card key={item.id} testId={`create-post-${item.platform.toLowerCase()}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-semibold text-slate-900">{item.platform}</p>
                  {item.title && <p className="text-sm text-slate-500">{item.title}</p>}
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  AI Draft
                </span>
              </div>

              <div className="mt-4">
                <CaptionEditor
                  value={item.caption}
                  onChange={(v) => updateCaption(item.id, v)}
                  onUsage={reloadUsage}
                  testIdPrefix={`create-caption-${item.platform.toLowerCase()}`}
                />
              </div>

              <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                {[["Hashtags", item.hashtags], ["Call to action", item.cta], ["Visual concept", item.graphic_note]].map(
                  ([label, value]) =>
                    value ? (
                      <div key={label} className="rounded-xl bg-[#FAFAFA] p-3">
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
                        <dd className="mt-1 text-sm text-slate-700">{value}</dd>
                      </div>
                    ) : null
                )}
              </dl>

              <div className="mt-4">
                <Btn
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(`${item.caption}\n\n${item.hashtags || ""}`.trim());
                    toast.success("Caption copied.");
                  }}
                  testId={`create-copy-${item.platform.toLowerCase()}`}
                >
                  <Copy className="h-4 w-4" /> Copy caption
                </Btn>
              </div>
            </Card>
          ))}
          <p className="text-sm text-slate-500">
            These drafts are saved in My Content, where you can approve them.
          </p>
        </div>
      )}
    </div>
  );
}
